import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { opensUpward } from './popupPlacement';
import './DatePicker.css';

// Themed calendar to replace native <input type="date"> — the browser's own
// picker (and its "dd-mm-yyyy" placeholder) is OS-native chrome that ignores
// the admin theme entirely. `value`/`onChange` use plain 'YYYY-MM-DD' strings,
// same as a native date input, so callers don't need to change how they store it.
const pad = n => String(n).padStart(2, '0');
const toISO = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parseISO = value => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};
const formatDisplay = value => {
    const d = parseISO(value);
    return d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
};

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// Years run from a century back (comfortably covers a date-of-birth field)
// to a decade ahead (covers future event bookings etc.) — generated once,
// not tied to any particular caller's use case.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 111 }, (_, i) => CURRENT_YEAR + 10 - i);

const DatePicker = ({ value, onChange, placeholder = 'Select date', min = '', max = '' }) => {
    const [open, setOpen] = useState(false);
    const [pickerMode, setPickerMode] = useState('days'); // 'days' | 'months' | 'years'
    const [dropUp, setDropUp] = useState(false); // open above the button when there is no room below
    const selected = parseISO(value);
    const minDate = parseISO(min);
    const maxDate = parseISO(max);
    const [viewDate, setViewDate] = useState(selected || new Date());
    const rootRef = useRef(null);
    const yearListRef = useRef(null);
    const activeYearRef = useRef(null);

    useEffect(() => {
        if (!open) { setPickerMode('days'); return undefined; }
        setViewDate(selected || new Date());
        const onDocClick = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        const onKey = e => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (pickerMode === 'years' && activeYearRef.current) {
            activeYearRef.current.scrollIntoView({ block: 'center' });
        }
    }, [pickerMode]);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first grid
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [...Array(startOffset).fill(null), ...Array(daysInMonth)].map((_, i) => (i < startOffset ? null : i - startOffset + 1));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isToday = d => d && year === today.getFullYear() && month === today.getMonth() && d === today.getDate();
    const isSelected = d => d && selected && year === selected.getFullYear() && month === selected.getMonth() && d === selected.getDate();
    const isOutOfRange = date => (minDate && date < minDate) || (maxDate && date > maxDate);
    const canViewMonth = (targetYear, targetMonth) => {
        const first = new Date(targetYear, targetMonth, 1);
        const last = new Date(targetYear, targetMonth + 1, 0);
        return !(minDate && last < minDate) && !(maxDate && first > maxDate);
    };
    const canViewYear = targetYear =>
        (!minDate || targetYear >= minDate.getFullYear())
        && (!maxDate || targetYear <= maxDate.getFullYear());

    return (
        <div className="ui-datepicker" ref={rootRef}>
            <button type="button" className={`ui-datepicker-trigger ${open ? 'is-open' : ''}`}
                onClick={(event) => {
                    if (!open) setDropUp(opensUpward(event.currentTarget, 330)); // the calendar is about 330px tall
                    setOpen(v => !v);
                }}>
                <span className={selected ? 'ui-datepicker-value' : 'ui-datepicker-placeholder'}>
                    {selected ? formatDisplay(value) : placeholder}
                </span>
                <CalendarDays size={15} className="ui-datepicker-icon" />
            </button>
            {open && (
                <div className={`ui-datepicker-panel ${dropUp ? 'is-up' : ''}`}>
                    <div className="ui-datepicker-header">
                        <button
                            type="button"
                            className="ui-datepicker-nav"
                            onClick={() => setViewDate(new Date(year, month - 1, 1))}
                            aria-label="Previous month"
                            disabled={pickerMode !== 'days' || !canViewMonth(year, month - 1)}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="ui-datepicker-title-group">
                            <button
                                type="button"
                                className="ui-datepicker-title-btn"
                                onClick={() => setPickerMode(m => m === 'months' ? 'days' : 'months')}
                            >
                                {MONTHS[month]}
                            </button>
                            <button
                                type="button"
                                className="ui-datepicker-title-btn"
                                onClick={() => setPickerMode(m => m === 'years' ? 'days' : 'years')}
                            >
                                {year}
                            </button>
                        </span>
                        <button
                            type="button"
                            className="ui-datepicker-nav"
                            onClick={() => setViewDate(new Date(year, month + 1, 1))}
                            aria-label="Next month"
                            disabled={pickerMode !== 'days' || !canViewMonth(year, month + 1)}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    {pickerMode === 'months' && (
                        <div className="ui-datepicker-month-grid">
                            {MONTHS.map((m, i) => (
                                <button
                                    type="button"
                                    key={m}
                                    className={`ui-datepicker-month-cell ${i === month ? 'is-selected' : ''}`}
                                    disabled={!canViewMonth(year, i)}
                                    onClick={() => { setViewDate(new Date(year, i, 1)); setPickerMode('days'); }}
                                >
                                    {m.slice(0, 3)}
                                </button>
                            ))}
                        </div>
                    )}

                    {pickerMode === 'years' && (
                        <div className="ui-datepicker-year-list" ref={yearListRef}>
                            {YEAR_LIST.map(y => (
                                <button
                                    type="button"
                                    key={y}
                                    ref={y === year ? activeYearRef : null}
                                    className={`ui-datepicker-year-cell ${y === year ? 'is-selected' : ''}`}
                                    disabled={!canViewYear(y)}
                                    onClick={() => { setViewDate(new Date(y, month, 1)); setPickerMode('days'); }}
                                >
                                    {y}
                                </button>
                            ))}
                        </div>
                    )}

                    {pickerMode === 'days' && (
                        <>
                            <div className="ui-datepicker-weekdays">
                                {WEEKDAYS.map(w => <span key={w}>{w}</span>)}
                            </div>
                            <div className="ui-datepicker-grid">
                                {cells.map((d, i) => d ? (
                                    <button
                                        type="button"
                                        key={i}
                                        className={`ui-datepicker-day ${isSelected(d) ? 'is-selected' : ''} ${isToday(d) ? 'is-today' : ''}`}
                                        disabled={isOutOfRange(new Date(year, month, d))}
                                        onClick={() => { onChange(toISO(year, month, d)); setOpen(false); }}
                                    >
                                        {d}
                                    </button>
                                ) : <span key={i} className="ui-datepicker-day-empty" />)}
                            </div>
                        </>
                    )}

                    <div className="ui-datepicker-footer">
                        <button type="button" className="ui-datepicker-footer-btn" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>
                        <button type="button" className="ui-datepicker-footer-btn" disabled={isOutOfRange(today)} onClick={() => {
                            const t = new Date();
                            setViewDate(t);
                            setPickerMode('days');
                            onChange(toISO(t.getFullYear(), t.getMonth(), t.getDate()));
                            setOpen(false);
                        }}>Today</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
